import { ReactElement, useState } from 'react';
import { Badge, Popover, Select, SelectProps } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { isHasAccess } from 'features/validateAccess';
import { useOpenTimesheetPeriodStateMutation, useCloseTimesheetPeriodStateMutation } from 'entities/timesheet';

import { getTimesheetsClosePermission, getTimesheetsReopenPermission } from '../../Timesheets.permissions';
import { ProjectRow } from '../../TimesheetsTable.types';
import { ConfirmPopoverContent } from '../ConfirmPopoverContent';
import { WarningPopoverContent } from '../WarningPopoverContent';

import styles from './ProjectStatusCell.module.scss';

const ProjectStatusCell = ({ record }: { record: ProjectRow }): ReactElement => {
  const { t } = useTranslation('Timesheet');

  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('accountId') ?? '';
  const projectId = searchParams.get('projectId') ?? '';

  const canReopenTimesheet = isHasAccess(getTimesheetsReopenPermission(accountId, projectId));
  const canCloseTimesheet = isHasAccess(getTimesheetsClosePermission(accountId, projectId));

  const canReopenProject = canReopenTimesheet && record.periodState === 'CLOSED';
  const canCloseProject = canCloseTimesheet && record.periodState === 'OPEN';

  const isStatusReadOnly = !canReopenProject && !canCloseProject;

  const hasClosedChildAccounts = !record.children.some((ass) => ass.status === 'NOT_APPROVED');
  const isParentAccountClosed = record.parentStatus === 'CLOSED';

  const [open, setOpen] = useState(false);
  const selectValue = record.periodState === 'OPEN' ? 'true' : 'false';

  const { mutate: openTimesheetPeriodStateMutation } = useOpenTimesheetPeriodStateMutation(record.periodId);
  const { mutate: closeTimesheetPeriodStateMutation } = useCloseTimesheetPeriodStateMutation(record.periodId);

  const onChange: SelectProps['onChange'] = (value) => {
    if (hasClosedChildAccounts) {
      if (isParentAccountClosed && value === 'true') {
        setOpen(true);
        return;
      }
      if (value === 'true') {
        openTimesheetPeriodStateMutation();
      } else {
        closeTimesheetPeriodStateMutation();
      }
    } else {
      setOpen(true);
    }
  };

  const handleOk = (): void => {
    if (selectValue === 'false') {
      openTimesheetPeriodStateMutation();
    } else {
      closeTimesheetPeriodStateMutation();
    }
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean): void => {
    setOpen(newOpen);
  };

  return (
    <div className={styles.projectStatusCell}>
      <Badge
        status={selectValue === 'true' ? 'default' : 'success'}
        className={selectValue === 'true' ? styles.badge : ''}
      />
      {isStatusReadOnly ? (
        <span className={styles.statusLabel}>
          {record.periodState === 'OPEN'
            ? t('timesheetTable.select.opened.label')
            : t('timesheetTable.select.closed.label')}
        </span>
      ) : (
        <Popover
          open={open}
          placement="bottom"
          arrow={{ pointAtCenter: true }}
          onOpenChange={handleOpenChange}
          content={
            isParentAccountClosed ? (
              <ConfirmPopoverContent
                handleCancel={() => setOpen(false)}
                handleOk={handleOk}
                title="Timesheet:projectStatusConfirmPopoverContent.text"
              />
            ) : (
              <WarningPopoverContent handleOk={() => setOpen(false)} title="projectWarningPopoverContent.text" />
            )
          }
          trigger={open ? ['click'] : []}
        >
          <Select
            value={selectValue}
            className={classNames(styles.select, { [styles.openedStatusSelect]: selectValue === 'true' })}
            onChange={onChange}
          >
            <Select.Option value="true">{t('timesheetTable.select.opened.label')}</Select.Option>
            <Select.Option value="false">{t('timesheetTable.select.closed.label')}</Select.Option>
          </Select>
        </Popover>
      )}
    </div>
  );
};

export { ProjectStatusCell };
